/**
 * Variant Media Refresh - AJAX section reloading
 * Reloads the product section when variants change to show filtered media
 * 
 * @version 1.0.0
 * @author Shopify Theme Development
 */

class VariantMediaRefresh {
  constructor() {
    this.isRefreshing = false;
    this.refreshTimeout = null;
    this.lastVariantId = null;
    this.init();
  }

  /**
   * Initialise the variant media refresh system
   */
  init() {
    this.productContainer = document.querySelector('[data-variant-filter="true"]');
    
    if (!this.productContainer) {
      console.log('Variant media filter not enabled for this product');
      return;
    }

    this.sectionId = this.getSectionId();
    if (!this.sectionId) {
      console.error('Could not determine section ID for variant media refresh');
      return;
    }

    this.setupEventListeners();
    this.storeInitialState();
    
    console.log('Variant media refresh initialised for section:', this.sectionId);
  }

  /**
   * Get the section ID from the product page container
   * @returns {string|null} Section ID or null if not found
   */
  getSectionId() {
    const productPage = document.querySelector('[id^="product-"]');
    if (productPage && productPage.id) {
      return productPage.id.replace('product-', '');
    }
    
    // Fallback: try to extract from script tags
    const filterDataScript = document.querySelector('[id^="filtered-media-data-"]');
    if (filterDataScript && filterDataScript.id) {
      return filterDataScript.id.replace('filtered-media-data-', '');
    }
    
    return null;
  }

  /**
   * Store the initial variant state to prevent unnecessary refreshes
   */
  storeInitialState() {
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    const variantIdInput = form?.querySelector('input[name="id"]');
    this.lastVariantId = variantIdInput?.value || null;
  }

  /**
   * Set up event listeners for variant changes
   */
  setupEventListeners() {
    // Listen for variant input changes (radio buttons, selects)
    const variantInputs = document.querySelectorAll(
      'input[class*="product-variant-value"], select[class*="product-variant-container"], input[name*="options"]'
    );
    
    variantInputs.forEach(input => {
      const eventType = input.type === 'radio' || input.type === 'checkbox' ? 'change' : 'change';
      
      input.addEventListener(eventType, (e) => {
        console.log('Variant input changed:', e.target.value);
        this.scheduleRefresh();
      });
    });

    // Listen for swatch changes
    const swatches = document.querySelectorAll('[data-variant-swatch="true"]');
    swatches.forEach(swatch => {
      swatch.addEventListener('change', (e) => {
        console.log('Swatch changed:', e.target.value);
        this.scheduleRefresh();
      });
    });

    // Listen for variant picker changes (product-variants component).
    // The component dispatches a plain 'VARIANT_CHANGE' event (see
    // component-product-form.js) — not 'variant:change'.
    const productVariants = document.querySelector('product-variants');
    if (productVariants) {
      productVariants.addEventListener('VARIANT_CHANGE', () => {
        console.log('Product variants changed');
        this.scheduleRefresh();
      });
    }

    // Listen for hidden variant ID changes (Shopify's native behaviour).
    // ProductVariants.updateVariantInput() sets `input.value` as a JS
    // property (not the DOM attribute), so a MutationObserver watching
    // the 'value' attribute never fires. It does, however, dispatch a
    // real 'change' event on the input itself — listen for that instead.
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (form) {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (variantIdInput) {
        variantIdInput.addEventListener('change', (e) => {
          console.log('Hidden variant ID changed:', e.target.value);
          this.scheduleRefresh();
        });
      }
    }
  }

  /**
   * Schedule a refresh with debouncing to prevent excessive requests
   */
  scheduleRefresh() {
    if (this.isRefreshing) {
      console.log('Refresh already in progress, skipping...');
      return;
    }

    // Clear any existing timeout
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    // Schedule refresh with short delay to allow variant selection to complete
    this.refreshTimeout = setTimeout(() => {
      this.refreshProductSection();
    }, 150);
  }

  /**
   * Refresh the product section via AJAX
   */
  async refreshProductSection() {
    if (!this.sectionId || this.isRefreshing) return;

    this.isRefreshing = true;

    try {
      // Get current variant ID
      const currentVariantId = this.getCurrentVariantId();
      
      if (!currentVariantId) {
        console.warn('No variant ID found, cannot refresh media');
        return;
      }

      // Skip refresh if variant hasn't actually changed
      if (currentVariantId === this.lastVariantId) {
        console.log('Variant unchanged, skipping refresh');
        return;
      }

      console.log('Refreshing media for variant:', currentVariantId);

      // Build URL for section refresh
      const refreshUrl = this.buildRefreshUrl(currentVariantId);
      
      // Fetch updated section
      const response = await fetch(refreshUrl, {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const html = await response.text();
      
      // Update the gallery
      await this.updateGallery(html);
      
      // Store the new variant ID
      this.lastVariantId = currentVariantId;
      
      console.log('Media gallery updated successfully for variant:', currentVariantId);

    } catch (error) {
      console.error('Failed to refresh variant media:', error);
      
      // Optionally show user-friendly error
      this.showRefreshError();
      
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Get the current variant ID from the form
   * @returns {string|null} Current variant ID or null
   */
  getCurrentVariantId() {
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    const variantIdInput = form?.querySelector('input[name="id"]');
    return variantIdInput?.value || null;
  }

  /**
   * Build the URL for fetching the refreshed section
   * @param {string} variantId - Current variant ID
   * @returns {string} Complete refresh URL
   */
  buildRefreshUrl(variantId) {
    const currentUrl = new URL(window.location);
    
    // Clear existing section_id parameter
    currentUrl.searchParams.delete('section_id');
    
    // Set variant and section parameters
    currentUrl.searchParams.set('variant', variantId);
    currentUrl.searchParams.set('section_id', this.sectionId);
    
    return currentUrl.toString();
  }

  /**
   * Update the gallery with new HTML content
   * @param {string} html - Fresh HTML from the server
   */
  async updateGallery(html) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Find the new gallery elements
    const newGallery = tempDiv.querySelector(`#css-slider-${this.sectionId}`);
    const newThumbnails = tempDiv.querySelector('.product-gallery__thumbnails');
    const newFilterData = tempDiv.querySelector(`#filtered-media-data-${this.sectionId}`);

    if (!newGallery) {
      throw new Error('New gallery not found in response');
    }

    // Replace gallery
    const currentGallery = document.querySelector(`#css-slider-${this.sectionId}`);
    if (currentGallery) {
      currentGallery.replaceWith(newGallery);
    } else {
      throw new Error('Current gallery not found');
    }

    // Replace thumbnails if they exist
    if (newThumbnails) {
      const currentThumbnails = document.querySelector('.product-gallery__thumbnails');
      if (currentThumbnails) {
        currentThumbnails.replaceWith(newThumbnails);
      }
    }

    // Update filter data
    if (newFilterData) {
      const currentFilterData = document.querySelector(`#filtered-media-data-${this.sectionId}`);
      if (currentFilterData) {
        currentFilterData.replaceWith(newFilterData);
      }
    }

    // Reinitialise slider functionality
    await this.reinitialiseSlider();
  }

  /**
   * Reinitialise the CSS slider after content update
   */
  async reinitialiseSlider() {
    const galleryElement = document.querySelector(`#css-slider-${this.sectionId}`);
    
    if (!galleryElement) {
      console.warn('Gallery element not found for reinitialisation');
      return;
    }

    // Wait for any pending DOM updates
    await new Promise(resolve => setTimeout(resolve, 50));

    // Try multiple reinitialisation approaches
    try {
      // Method 1: Custom event for theme's slider system
      if (window.CSSSlider) {
        const event = new CustomEvent('slider:reinit', {
          detail: { element: galleryElement }
        });
        document.dispatchEvent(event);
      }

      // Method 2: Direct slider reinitialisation if available
      if (galleryElement.slider && typeof galleryElement.slider.refresh === 'function') {
        galleryElement.slider.refresh();
      }

      // Method 3: Trigger resize to recalculate slider dimensions
      window.dispatchEvent(new Event('resize'));

      console.log('Slider reinitialised successfully');

    } catch (error) {
      console.warn('Could not reinitialise slider:', error);
    }
  }

  /**
   * Show a user-friendly error message when refresh fails
   */
  showRefreshError() {
    // This could be enhanced with a toast notification or subtle error display
    console.log('Media refresh failed - this is typically not visible to customers');
  }
}

/**
 * Initialise variant media refresh when DOM is ready
 */
function initVariantMediaRefresh() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      new VariantMediaRefresh();
    });
  } else {
    new VariantMediaRefresh();
  }
}

// Auto-initialise
//initVariantMediaRefresh(); 

// Export for manual initialisation if needed
window.VariantMediaRefresh = VariantMediaRefresh; 